// 04 · Prélèvements — caisses traditionnelles.
//
// Trois choses à savoir avant de lire ce fichier :
//
//   • coffrePrelev et paperPrelev ne sont pas des montants, ce sont des NOMBRES
//     D'ENVELOPPES. Une enveloppe vaut 500 € — le parseur partagé pose
//     prelevCount = round(prelev / 500). Le contrôle du jour est donc un
//     comptage : enveloppes caissiers présentes + coffre = feuille papier.
//     Un écart de 1 se lit « il manque une enveloppe », pas « il manque 1 € ».
//     L'écran affiche quand même la CONSÉQUENCE en euros (écart × 500), parce
//     qu'une enveloppe manquante n'est pas une unité de plus ou de moins :
//     c'est 500 € qui ne sont nulle part.
//
//   • le cumul court du MARDI au LUNDI (getPrevWeekStart), parce que c'est le
//     rythme de la remise d'espèces, pas celui de la semaine civile.
//
//   • la concordance de cet écran est celle de la V2, vérifiée ligne à ligne :
//     digital = enveloppes caissiers PRÉSENTES + coffre, écart = digital −
//     papier. C'est pourquoi on ne se sert pas de l'écart de
//     domain/derive → etatPrelevements(), qui ne compare que coffre et papier
//     et oublie les enveloppes caissiers (voir le retour d'agent). On lui
//     emprunte en revanche VALEUR_ENVELOPPE : la valeur d'une enveloppe n'a
//     pas à être écrite deux fois.
//
// L'état d'une enveloppe vit dans rapport.rapportState[opId]['prelev_<i>'],
// objet partagé avec le dépouillement V2 : on réécrit la clé `rapportState`
// entière à chaque bascule, exactement comme la V2, jamais une sous-clé.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getPrevWeekStart, getWeekDates } from 'sotcp-core/dayUtils'
import { useRun } from '@/data/RunProvider'
import * as api from '@/data/api'
import { addDays, fmtEur, formatMedium, todayIso } from '@/lib/format'
import { messageErreur } from '@/lib/errors'
import { couleurEcartUnites, fmtEcart, fmtEcartUnites } from '@/domain/ecart'
import { VALEUR_ENVELOPPE } from '@/domain/derive'
import { Card, Chip, Kpis, Lamp, Notice, Vide } from '@/ui/primitives'
import type { Couleur } from '@/ui/primitives'
import type { Rapport } from '@/domain/types'
import type { PropsEtape } from './types'

/** Fenêtre du scan « dernier inventaire validé », comme la V2. */
const FENETRE_INVENTAIRE = 14
/** Un compteur bouge par à-coups : on ne poste qu'une fois la main levée. */
const DELAI_SAISIE = 400

type Cle = 'coffrePrelev' | 'paperPrelev'

interface OpPrelev {
  id: string
  nom: string
  badge: string
  prelev: number
  nb: number
  irregulier: boolean
}

interface Enveloppe {
  cle: string
  opId: string
  nom: string
  badge: string
  index: number
  total: number
  montant: number
  manquant: boolean
}

interface ResumeJour { presents: number; manquants: number; coffre: number; papier: number }
interface LigneSemaine extends ResumeJour { date: string; saisi: boolean; aujourdhui: boolean }
interface Indice { date: string; nombre: number }

type EtatEnveloppe = { manquant?: boolean }
type EtatOps = Record<string, Record<string, EtatEnveloppe>>

const objet = (v: unknown): boolean => !!v && typeof v === 'object' && !Array.isArray(v)

/**
 * Pendant de couleurEcartUnites pour une <Lamp> : @/domain/ecart expose
 * lampeEcart (euros) mais pas son équivalent en unités. Même échelle, à
 * l'identique — 0 conforme, 1 à surveiller, 2 et plus critique.
 */
const lampeUnites = (n: number): Couleur => {
  const a = Math.abs(Number(n) || 0)
  return a === 0 ? 'ok' : a === 1 ? 'warn' : 'stop'
}

/**
 * Forme courte d'un écart en unités, pour un KPI ou une puce : la colonne fait
 * 120 px et « +2 enveloppes » n'y tient pas. Le mot complet reste dans le
 * bandeau, via fmtEcartUnites. Le signe suit la même convention que le module
 * (vrai signe moins), jamais le trait d'union du clavier.
 */
const ecartCourt = (n: number): string => (n === 0 ? '0' : `${n > 0 ? '+' : '−'}${Math.abs(n)}`)

function lireOps(r: Rapport | null): OpPrelev[] {
  const src = Array.isArray(r?.rapportOps) ? (r.rapportOps as Record<string, unknown>[]) : []
  return src.map((o) => ({
    id: String(o.id ?? ''),
    nom: String(o.nom ?? '—'),
    badge: String(o.badge ?? ''),
    prelev: Number(o.prelev) || 0,
    nb: Math.max(0, Math.trunc(Number(o.prelevCount) || 0)),
    irregulier: o.prelevIrregular === true,
  }))
}

const lireEtat = (r: Rapport | null): EtatOps =>
  objet(r?.rapportState) ? (r?.rapportState as EtatOps) : {}

function lireEnveloppes(r: Rapport | null): Enveloppe[] {
  const etat = lireEtat(r)
  const liste: Enveloppe[] = []
  for (const o of lireOps(r)) {
    // La boucle ne tourne que si nb ≥ 1 : la division ci-dessous ne peut donc
    // pas rencontrer un zéro.
    for (let i = 0; i < o.nb; i++) {
      liste.push({
        cle: `${o.id}-${i}`,
        opId: o.id, nom: o.nom, badge: o.badge,
        index: i, total: o.nb,
        montant: o.prelev / o.nb,
        manquant: etat[o.id]?.[`prelev_${i}`]?.manquant === true,
      })
    }
  }
  return liste
}

/** Le comptage d'une journée passée, tel qu'il est figé dans son rapport. */
function resumer(r: Rapport | null): ResumeJour {
  const enveloppes = lireEnveloppes(r)
  const manquants = enveloppes.filter((e) => e.manquant).length
  return {
    presents: enveloppes.length - manquants,
    manquants,
    coffre: Number(r?.coffrePrelev) || 0,
    papier: Number(r?.paperPrelev) || 0,
  }
}

interface InventaireLu { status?: string; prelevements_j?: unknown[] }

const lireInventaire = (r: Rapport | null): InventaireLu | null =>
  objet(r?.inventaireCoffre) ? (r?.inventaireCoffre as InventaireLu) : null

export default function Prelevements({ rapport, lectureSeule }: PropsEtape) {
  const { date, ecrireRapport } = useRun()

  const enveloppes = useMemo(() => lireEnveloppes(rapport), [rapport])
  const ops = useMemo(() => lireOps(rapport), [rapport])
  const coffreEnregistre = Number(rapport?.coffrePrelev) || 0
  const papierEnregistre = Number(rapport?.paperPrelev) || 0

  // Le brouillon tient la valeur tapée pendant que la RPC est en route ; il
  // s'efface une fois l'écriture faite, et l'affichage retombe alors sur le
  // rapport — donc sur la valeur d'avant si le serveur a refusé.
  const [brouillon, setBrouillon] = useState<Partial<Record<Cle, number>>>({})
  const minuteurs = useRef<Partial<Record<Cle, number>>>({})

  const [indice, setIndice] = useState<Indice | null>(null)
  // « La main a été rendue au compteur » : dès qu'on a repris l'indice OU tapé
  // une valeur, l'indice ne revient plus. Sans ce verrou, remettre le compteur
  // à 0 ferait réapparaître l'affichage en lecture seule par-dessus la saisie
  // qu'on vient de faire — le compteur disparaîtrait sous les doigts.
  const [mainRendue, setMainRendue] = useState(false)
  const [reprise, setReprise] = useState(false)
  const [semaine, setSemaine] = useState<Record<string, ResumeJour>>({})
  const [histoPret, setHistoPret] = useState(false)
  const [histoErreur, setHistoErreur] = useState<string | null>(null)

  // L'auto-remplissage ne se pose que si le coffre n'a jamais été compté : on
  // fige la question au montage, sinon le scan repartirait à chaque frappe.
  const scruter = useRef(coffreEnregistre === 0).current

  const enregistrer = useCallback(async (libelle: string, patch: Record<string, unknown>) => {
    try {
      await ecrireRapport(libelle, patch, () => api.patcherRapport(date, patch), '04')
    } catch { /* le provider a déjà posé le message et remis l'état d'avant */ }
  }, [date, ecrireRapport])

  // La valeur reprise de la veille et la valeur retenue sont CONSERVÉES toutes
  // les deux, avec leur écart. Si la correction écrasait la proposition, plus
  // personne ne saurait, le lendemain, pourquoi le compte ne tombe pas — et
  // l'écart de coffre du jour d'après serait cherché au mauvais endroit.
  const tracer = useCallback((corrige: number) => {
    const attendu = indice?.nombre ?? null
    if (attendu === null) return
    const ecart = corrige - attendu
    void api.toucherEtape(date, '04', {
      prelevCoffre: { attendu, source: indice?.date ?? null, corrige, ecart },
    }).catch(() => {})
    if (ecart !== 0) {
      // Au journal de la journée, pas seulement dans l'avancement : une
      // correction manuelle d'un chiffre repris est un geste qui s'explique.
      void api.noterJournal(date,
        `Prélèvements coffre corrigés à la main : ${attendu} repris du ${indice?.date ?? '?'} → `
        + `${corrige} retenus (${ecart > 0 ? '+' : ''}${ecart} enveloppe${Math.abs(ecart) > 1 ? 's' : ''}, `
        + `${fmtEur(Math.abs(ecart) * VALEUR_ENVELOPPE)}).`, '04').catch(() => {})
    }
  }, [date, indice])

  const poster = useCallback(async (cle: Cle, n: number, libelle?: string) => {
    await enregistrer(
      libelle ?? (cle === 'coffrePrelev' ? 'comptage des enveloppes du coffre' : 'feuille papier'),
      { [cle]: n },
    )
    if (cle === 'coffrePrelev') tracer(n)
    setBrouillon((b) => {
      if (b[cle] !== n) return b   // une frappe plus récente attend déjà son tour
      const suite = { ...b }
      delete suite[cle]
      return suite
    })
  }, [enregistrer, tracer])

  const saisir = useCallback((cle: Cle, valeur: number) => {
    // Garde de fond, en plus du `disabled` des champs : le verrou peut passer à
    // quelqu'un d'autre pendant qu'un minuteur court, et une saisie ne doit
    // jamais partir depuis un écran devenu en lecture seule.
    if (lectureSeule) return
    const n = Math.max(0, Math.trunc(valeur))
    if (cle === 'coffrePrelev') setMainRendue(true)
    setBrouillon((b) => ({ ...b, [cle]: n }))
    window.clearTimeout(minuteurs.current[cle])
    // Le minuteur n'est volontairement PAS annulé au démontage : changer
    // d'étape juste après avoir tapé ne doit pas perdre la dernière saisie.
    minuteurs.current[cle] = window.setTimeout(() => { void poster(cle, n) }, DELAI_SAISIE)
  }, [lectureSeule, poster])

  /** Entrée : ne pas attendre les 400 ms, poster tout de suite. */
  const validerSaisie = useCallback((cle: Cle) => {
    const n = brouillon[cle]
    if (lectureSeule || n === undefined) return
    window.clearTimeout(minuteurs.current[cle])
    void poster(cle, n)
  }, [brouillon, lectureSeule, poster])

  /** Échap : renoncer à la frappe en cours, l'affichage retombe sur le rapport. */
  const annulerSaisie = useCallback((cle: Cle) => {
    window.clearTimeout(minuteurs.current[cle])
    setBrouillon((b) => {
      if (b[cle] === undefined) return b
      const suite = { ...b }
      delete suite[cle]
      return suite
    })
  }, [])

  const basculer = useCallback((e: Enveloppe) => {
    if (lectureSeule) return
    const etat = lireEtat(rapport)
    const courant = etat[e.opId] ?? {}
    const enveloppe = courant[`prelev_${e.index}`] ?? {}
    const suite: EtatOps = {
      ...etat,
      [e.opId]: { ...courant, [`prelev_${e.index}`]: { ...enveloppe, manquant: !e.manquant } },
    }
    void enregistrer(`enveloppe ${e.index + 1} de ${e.nom}`, { rapportState: suite })
  }, [enregistrer, lectureSeule, rapport])

  // ── La semaine et le dernier inventaire validé ───────────────────────────
  const debutSemaine = useMemo(() => getPrevWeekStart(date), [date])
  const joursSemaine = useMemo(() => getWeekDates(debutSemaine) as string[], [debutSemaine])

  useEffect(() => {
    let vivant = true
    setHistoPret(false)
    setHistoErreur(null)
    // Une journée peut servir aux deux besoins (cumul et indice) : on ne la
    // charge qu'une fois.
    const cache = new Map<string, Rapport | null>()
    const lire = async (d: string): Promise<Rapport | null> => {
      const vu = cache.get(d)
      if (vu !== undefined) return vu
      const j = await api.chargerJournee(d)
      const r = j?.rapport ?? null
      cache.set(d, r)
      return r
    }

    void (async () => {
      try {
        const premier = joursSemaine[0] ?? date
        const dernier = joursSemaine[6] ?? date
        const debutScan = addDays(date, -FENETRE_INVENTAIRE)
        // Une seule requête légère (date + statut) pour savoir quelles journées
        // existent : inutile d'aller chercher le rapport d'un jour sans journée.
        const journees = await api.listerJournees(
          debutScan < premier ? debutScan : premier,
          dernier > date ? dernier : date,
        )
        const existent = new Set(journees.map((j) => j.date))

        const resumes: Record<string, ResumeJour> = {}
        for (const d of joursSemaine) {
          if (d === date || !existent.has(d)) continue
          resumes[d] = resumer(await lire(d))
          if (!vivant) return
        }
        if (!vivant) return
        setSemaine(resumes)

        if (scruter) {
          const candidats = journees
            .map((j) => j.date)
            .filter((d) => d < date)
            .sort()
            .reverse()
          for (const d of candidats) {
            const inv = lireInventaire(await lire(d))
            if (!vivant) return
            if (inv?.status === 'validated') {
              setIndice({ date: d, nombre: (inv.prelevements_j ?? []).length })
              break
            }
          }
        }
        if (vivant) setHistoPret(true)
      } catch (e) {
        if (vivant) { setHistoErreur(messageErreur(e)); setHistoPret(true) }
      }
    })()

    return () => { vivant = false }
  }, [date, joursSemaine, scruter])

  // ── Le jour ──────────────────────────────────────────────────────────────
  // Tant que l'indice n'a pas été repris, il n'est PAS enregistré — il compte
  // quand même dans la concordance affichée, comme dans la V2.
  const indiceActif = !mainRendue && coffreEnregistre === 0 && (indice?.nombre ?? 0) > 0
  const coffre = brouillon.coffrePrelev ?? (indiceActif ? (indice?.nombre ?? 0) : coffreEnregistre)
  const papier = brouillon.paperPrelev ?? papierEnregistre

  const manquants = enveloppes.filter((e) => e.manquant).length
  const presents = enveloppes.length - manquants
  const digital = presents + coffre
  const ecart = digital - papier
  // Ce que l'écart pèse réellement : une enveloppe n'est pas une unité, c'est
  // 500 € de la caisse principale.
  const ecartEuros = ecart * VALEUR_ENVELOPPE
  const aPapier = papier > 0
  const concorde = aPapier && ecart === 0
  const irreguliers = ops.filter((o) => o.irregulier)
  // Journée encore vide (rien d'importé, rien de compté) : la V2 ne montre
  // aucun bandeau de concordance. Réclamer la feuille papier avant même que le
  // rapport opérateur soit là serait un reproche adressé à personne — mais on
  // ne laisse pas l'écran muet pour autant : il dit ce qu'on peut faire.
  const aMatiere = enveloppes.length > 0 || coffre > 0 || papier > 0

  // « Reprendre ce comptage » : la valeur reprise de l'inventaire est
  // ENREGISTRÉE au passage, comme la V2 — sans quoi une valeur juste, seulement
  // affichée, serait perdue si la personne ne touche pas au compteur.
  const reprendreIndice = () => {
    if (lectureSeule || reprise) return
    const n = coffre
    setReprise(true)
    setMainRendue(true)
    // Le brouillon tient le nombre PENDANT l'écriture. Sans lui, mainRendue
    // fait disparaître l'indice alors que coffreEnregistre vaut encore 0 : le
    // compteur retombe visiblement à zéro le temps de l'aller-retour serveur,
    // et on croit avoir perdu le comptage qu'on vient d'accepter.
    setBrouillon((b) => ({ ...b, coffrePrelev: n }))
    void poster('coffrePrelev', n, 'reprise du comptage du coffre')
      .finally(() => setReprise(false))
  }

  // L'autre issue : la proposition est fausse, on compte soi-même. Elle
  // n'écrit rien — reprendre un chiffre qu'on sait faux pour pouvoir le
  // corriger était le seul chemin vers le compteur, et c'était un piège.
  const compterSoiMeme = () => {
    if (lectureSeule) return
    setMainRendue(true)
  }

  // ── La semaine ───────────────────────────────────────────────────────────
  const aujourdhui = todayIso()
  // La semaine ne se juge que si elle est entièrement chargée. Sans cette
  // garde, une erreur réseau laissait `semaine` vide, les autres jours
  // comptaient pour zéro, et l'écran annonçait en rouge un « écart » qui
  // n'était qu'une panne de chargement.
  const semaineFiable = histoPret && histoErreur === null
  const lignes: LigneSemaine[] = joursSemaine.map((d) => {
    if (d === date) {
      return { date: d, saisi: true, aujourdhui: true, presents, manquants, coffre, papier }
    }
    const r = semaine[d]
    return r
      ? { date: d, saisi: true, aujourdhui: false, ...r }
      : { date: d, saisi: false, aujourdhui: false, presents: 0, manquants: 0, coffre: 0, papier: 0 }
  })

  // Les jours à venir ne comptent pas : un cumul qui additionne demain
  // annoncerait un écart qui n'existe pas encore. Le jour de la run, lui,
  // compte toujours — une run ouverte sur une date postérieure à aujourd'hui
  // se voyait sinon retirer son propre comptage du cumul.
  const cumul = lignes.filter((l) => l.aujourdhui || l.date <= aujourdhui).reduce(
    (a, l) => ({
      presents: a.presents + l.presents,
      manquants: a.manquants + l.manquants,
      coffre: a.coffre + l.coffre,
      papier: a.papier + l.papier,
    }),
    { presents: 0, manquants: 0, coffre: 0, papier: 0 },
  )
  const digitalSemaine = cumul.presents + cumul.coffre
  const ecartSemaine = digitalSemaine - cumul.papier
  const semaineAPapier = cumul.papier > 0
  const semaineJugeable = semaineFiable && semaineAPapier
  const semaineConcorde = semaineJugeable && ecartSemaine === 0

  return (
    <>
      <Kpis items={[
        {
          k: 'Enveloppes caissiers',
          v: `${presents} / ${enveloppes.length}`,
          // « 0 sur 0 · toutes présentes » annonçait un contrôle conforme là où
          // il n'y a rien à contrôler. Rien n'est vert tant que rien n'est là.
          s: enveloppes.length === 0 ? 'aucune annoncée'
            : manquants > 0 ? `${manquants} manquante${manquants > 1 ? 's' : ''} · ${fmtEcart(-manquants * VALEUR_ENVELOPPE)}`
            : 'toutes présentes',
          couleur: enveloppes.length === 0 ? undefined : couleurEcartUnites(manquants),
        },
        {
          k: 'Coffre', v: coffre,
          s: indiceActif ? 'repris de l’inventaire' : coffre === 0 ? 'à compter' : 'enveloppes comptées',
        },
        { k: 'Total digital', v: digital, s: 'caissiers + coffre', couleur: 'var(--act)' },
        { k: 'Feuille papier', v: aPapier ? papier : '—', s: aPapier ? undefined : 'à saisir' },
        {
          k: 'Écart', v: aPapier ? ecartCourt(ecart) : '—',
          s: !aPapier ? 'papier non saisi' : ecart === 0 ? 'concordance' : fmtEcart(ecartEuros),
          couleur: aPapier ? couleurEcartUnites(ecart) : undefined,
        },
      ]} />

      {!aMatiere ? (
        <Notice>
          <b>Rien à rapprocher pour l’instant.</b> Le rapport opérateur n’annonce aucune enveloppe
          caissier. Compte les enveloppes du coffre et saisis la feuille papier si elle existe —
          sinon l’étape se valide telle quelle.
        </Notice>
      ) : !aPapier ? (
        <Notice>
          <b>Saisis la feuille papier pour vérifier la concordance.</b> Tant qu’elle est à zéro,
          rien n’est comparé — le comptage du jour reste une déclaration sans contrôle.
        </Notice>
      ) : concorde ? (
        <Notice c="ok">
          <b>Concordance</b> — {presents} enveloppe{presents > 1 ? 's' : ''} caissier
          {presents > 1 ? 's' : ''} + {coffre} coffre = {papier} papier.
        </Notice>
      ) : (
        <Notice c={lampeUnites(ecart)}>
          <b>Écart de {fmtEcartUnites(ecart, 'enveloppe')}</b> — digital {digital} ≠ papier{' '}
          {papier}, soit <span className="mo">{fmtEcart(ecartEuros)}</span> au regard de la feuille.
          {manquants === 0 && ' Bascule ci-dessous les enveloppes qui ne sont pas au coffre pour situer l’écart.'}
        </Notice>
      )}

      {irreguliers.length > 0 && (
        <Notice c="warn">
          <b>{irreguliers.length} prélèvement{irreguliers.length > 1 ? 's' : ''} non multiple
          de {VALEUR_ENVELOPPE} €</b> — {irreguliers.map((o) => o.nom).join(', ')}. Avertissement{' '}
          <span className="mo">prelevIrregular</span> du parseur : il ne bloque rien, le nombre
          d’enveloppes est arrondi, mais le comptage de ces caissiers mérite un regard.
        </Notice>
      )}

      <Card
        titre="Le compte du jour"
        lampe={!aMatiere ? 'neutre' : !aPapier ? 'warn' : lampeUnites(ecart)}
        actions={<Chip>1 enveloppe = {VALEUR_ENVELOPPE} €</Chip>}
      >
        <div className="slot">
          <span className="nm">Enveloppes relevées au coffre</span>
          <span className="sp" />
          {indiceActif ? (
            <>
              <span className="mo" style={{ fontSize: 15 }}>{coffre}</span>
              {!lectureSeule && (
                <>
                  <button type="button" className="btn ok sm"
                          data-encours={reprise ? 'true' : undefined}
                          onClick={reprendreIndice}>
                    Reprendre ce comptage
                  </button>
                  <button type="button" className="btn sm" onClick={compterSoiMeme}>
                    Compter moi-même
                  </button>
                </>
              )}
            </>
          ) : (
            <Compteur valeur={coffre} sur={(n) => saisir('coffrePrelev', n)}
                      valider={() => validerSaisie('coffrePrelev')}
                      annuler={() => annulerSaisie('coffrePrelev')}
                      desactive={lectureSeule} libelle="enveloppes au coffre" />
          )}
        </div>

        {/* La correction ne remplace pas la reprise : les deux valeurs se lisent
            côte à côte, avec l'écart. Le lendemain, c'est cet écart qui
            expliquera un compte qui ne tombe pas. */}
        {indice && !indiceActif && coffre !== indice.nombre && (
          <div className="slot" style={{ background: 'var(--warn-w)' }}>
            <Lamp c="warn" />
            <span className="nm">Corrigé à la main</span>
            <span className="sp" />
            <span className="mo" style={{ color: 'var(--ink3)' }}>
              {indice.nombre} repris du {formatMedium(indice.date)}
            </span>
            <span className="mo">→ {coffre} retenu{coffre > 1 ? 's' : ''}</span>
            <span className="mo" style={{ color: couleurEcartUnites(coffre - indice.nombre) }}
                  title={`${Math.abs(coffre - indice.nombre) * VALEUR_ENVELOPPE} € d’écart avec la veille`}>
              {fmtEcartUnites(coffre - indice.nombre, 'enveloppe')}
            </span>
          </div>
        )}

        {indice && (indiceActif || indice.nombre > 0) && (
          <div className="slot" style={{ paddingTop: 0 }}>
            <span className="hint">
              {indiceActif ? (
                <>
                  Repris de l’inventaire validé du {formatMedium(indice.date)} —{' '}
                  {indice.nombre} enveloppe{indice.nombre > 1 ? 's' : ''} en prélèvements du jour.
                  Ce nombre n’est pas encore enregistré : « Reprendre ce comptage » l’inscrit et
                  rend la main sur le compteur, « Compter moi-même » n’inscrit rien.
                </>
              ) : (
                <>
                  Pour mémoire : l’inventaire validé du {formatMedium(indice.date)} comptait{' '}
                  {indice.nombre} enveloppe{indice.nombre > 1 ? 's' : ''} en prélèvements du jour.
                </>
              )}
            </span>
          </div>
        )}

        <div className="slot">
          <span className="nm">Feuille papier</span>
          <span className="sp" />
          <Compteur valeur={papier} sur={(n) => saisir('paperPrelev', n)}
                    valider={() => validerSaisie('paperPrelev')}
                    annuler={() => annulerSaisie('paperPrelev')}
                    desactive={lectureSeule} libelle="enveloppes sur la feuille papier" />
        </div>

        <div className="slot">
          <span className="nm">Total digital</span>
          <span className="sp" />
          <span className="mo" style={{ fontSize: 15 }}>
            {presents} + {coffre} = {digital}
          </span>
          {/* Le verdict tombe SOUS les doigts, pas en haut de page : quand on
              vient de taper la feuille papier, l'écran est scrollé sur les
              compteurs et remonter au KPI pour savoir si ça tombe juste est le
              geste de trop. */}
          {aPapier && (
            <Chip c={lampeUnites(ecart)}>
              {ecart === 0 ? 'concordance' : `${ecartCourt(ecart)} · ${fmtEcart(ecartEuros)}`}
            </Chip>
          )}
        </div>
      </Card>

      <div style={{ marginTop: 12 }}>
        <Card
          titre={`Enveloppes caissiers — ${enveloppes.length}`}
          // Une enveloppe manquante est un écart, pas une nuance de style :
          // deux manquantes valent 1 000 € et méritent le rouge, pas l'ambre.
          lampe={enveloppes.length === 0 ? 'neutre' : lampeUnites(manquants)}
          actions={
            <>
              {/* Vert = « rien ne manque ». « 7 présentes » en vert à côté de
                  « 3 manquantes » en rouge donnait un feu vert à une carte qui
                  porte un écart : la puce redevient neutre dès qu'il en manque
                  une. */}
              <Chip c={manquants === 0 && presents > 0 ? 'ok' : 'neutre'}>
                {presents} présente{presents > 1 ? 's' : ''}
              </Chip>
              {manquants > 0 && (
                <Chip c={lampeUnites(manquants)}>
                  {manquants} manquante{manquants > 1 ? 's' : ''}
                </Chip>
              )}
            </>
          }
        >
          {enveloppes.length === 0 ? (
            <Vide>
              Aucun prélèvement caissier dans le rapport opérateur du jour.
              {' '}Si l’import n’a pas encore été fait, reviens après l’étape 01 ; sinon la journée
              n’a réellement aucun prélèvement caissier et l’étape se valide en l’état.
            </Vide>
          ) : (
            <>
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(214px, 1fr))',
                gap: 1, background: 'var(--line)', borderBottom: '1px solid var(--line)',
              }}>
                {enveloppes.map((e) => (
                  <button
                    key={e.cle}
                    type="button"
                    onClick={() => basculer(e)}
                    disabled={lectureSeule}
                    // L'état « appuyé » d'une bascule, c'est l'action qu'elle
                    // porte : déclarer l'enveloppe manquante. L'inverse se
                    // lisait « présente = appuyée », ce qui annonçait le
                    // contraire de l'état au lecteur d'écran.
                    aria-pressed={e.manquant}
                    aria-label={`${e.nom}${e.total > 1 ? `, enveloppe ${e.index + 1} sur ${e.total}` : ''} — ${e.manquant ? 'déclarée manquante' : 'présente'}`}
                    title={e.manquant ? 'Déclarée manquante — cliquer pour la remettre présente'
                                      : 'Présente — cliquer pour la déclarer manquante'}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 11px',
                      textAlign: 'left', width: '100%',
                      background: e.manquant ? 'var(--stop-w)' : 'var(--surface)',
                    }}
                  >
                    <Lamp c={e.manquant ? 'stop' : 'ok'} />
                    <span style={{ minWidth: 0 }}>
                      <span style={{
                        display: 'block', fontSize: 12, fontWeight: 600,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        color: e.manquant ? 'var(--stop)' : 'var(--ink)',
                      }}>
                        {e.nom}
                        {e.total > 1 && (
                          <span style={{ color: 'var(--ink4)' }}> · env {e.index + 1}/{e.total}</span>
                        )}
                      </span>
                      <span className="mo" style={{ display: 'block', fontSize: 9.5, color: 'var(--ink4)' }}>
                        {e.badge ? `#${e.badge}` : 'SANS BADGE'}
                        {e.montant > 0 && ` · ${fmtEur(e.montant)}`}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
              <div className="hint" style={{ padding: '8px 12px' }}>
                {lectureSeule
                  ? 'Étape close : le comptage se lit, il ne se modifie plus.'
                  : 'Clique une enveloppe pour la basculer présente / manquante. Une enveloppe déclarée manquante sort du total digital.'}
              </div>
            </>
          )}
        </Card>
      </div>

      <div style={{ marginTop: 12 }}>
        <Card
          titre={`Cumul de la semaine — mardi ${formatMedium(joursSemaine[0] ?? debutSemaine)} → lundi ${formatMedium(joursSemaine[6] ?? date)}`}
          lampe={!semaineJugeable ? 'neutre' : lampeUnites(ecartSemaine)}
          actions={
            <Chip c={!semaineJugeable ? 'neutre' : lampeUnites(ecartSemaine)}>
              {!semaineFiable ? (histoErreur ? 'semaine incomplète' : 'chargement…')
                : !semaineAPapier ? 'en attente'
                : semaineConcorde ? 'concordance'
                : `écart ${ecartCourt(ecartSemaine)}`}
            </Chip>
          }
        >
          {histoErreur && (
            <div style={{ padding: '9px 12px', borderBottom: '1px solid var(--line)' }}>
              <span className="hint" style={{ color: 'var(--stop)' }}>
                Semaine incomplète — {histoErreur} Aucun écart hebdomadaire n’est annoncé tant que
                les journées manquent : ce serait accuser un chargement raté. Le compte du jour,
                lui, est à jour.
              </span>
            </div>
          )}

          <div style={{ padding: 11, borderBottom: '1px solid var(--line)' }}>
            <Kpis items={[
              {
                k: 'Caissiers', v: cumul.presents,
                s: cumul.manquants > 0 ? `${cumul.manquants} manquante${cumul.manquants > 1 ? 's' : ''}` : undefined,
                couleur: cumul.manquants > 0 ? couleurEcartUnites(cumul.manquants) : undefined,
              },
              { k: 'Coffre', v: cumul.coffre },
              { k: 'Digital', v: digitalSemaine, s: 'caissiers + coffre', couleur: 'var(--act)' },
              { k: 'Papier', v: semaineAPapier ? cumul.papier : '—' },
              {
                k: 'Écart', v: semaineJugeable ? ecartCourt(ecartSemaine) : '—',
                s: !semaineFiable ? 'semaine partielle'
                  : !semaineAPapier ? 'papier non saisi'
                  : ecartSemaine === 0 ? 'concordance'
                  : fmtEcart(ecartSemaine * VALEUR_ENVELOPPE),
                couleur: semaineJugeable ? couleurEcartUnites(ecartSemaine) : undefined,
              },
            ]} />
          </div>

          <div className="scrollx">
            <table className="t">
              <thead>
                <tr>
                  <th>Jour</th>
                  <th className="n">Caissiers</th><th className="n">Coffre</th>
                  <th className="n">Digital</th><th className="n">Papier</th>
                  <th>Concordance</th>
                </tr>
              </thead>
              <tbody>
                {lignes.map((l) => {
                  const jourDigital = l.presents + l.coffre
                  const jourEcart = jourDigital - l.papier
                  const jourAPapier = l.papier > 0
                  const aVenir = !l.aujourdhui && l.date > aujourdhui
                  return (
                    <tr key={l.date} style={aVenir ? { opacity: 0.45 } : undefined}>
                      <td>
                        <b>{formatMedium(l.date)}</b>
                        {l.aujourdhui && <> <Chip c="act">ce jour</Chip></>}
                      </td>
                      <td className="n">
                        {l.saisi ? l.presents : '—'}
                        {l.manquants > 0 && (
                          <span style={{ color: couleurEcartUnites(l.manquants) }}> (−{l.manquants})</span>
                        )}
                      </td>
                      <td className="n">{l.saisi ? l.coffre : '—'}</td>
                      <td className="n">{l.saisi ? jourDigital : '—'}</td>
                      <td className="n">{jourAPapier ? l.papier : '—'}</td>
                      <td>
                        {aVenir ? <span className="hint">à venir</span>
                          // Une journée qui n'a pas pu être chargée n'est pas
                          // une journée absente : le dire aurait fait passer
                          // une panne réseau pour un trou dans la semaine.
                          : !l.saisi ? (
                            <span className="hint">
                              {!histoPret ? 'chargement…' : histoErreur ? 'non chargée' : 'journée absente'}
                            </span>
                          )
                          : !jourAPapier ? <span className="hint">papier non saisi</span>
                          : <Chip c={lampeUnites(jourEcart)}>
                              {jourEcart === 0 ? 'ok' : ecartCourt(jourEcart)}
                            </Chip>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="hint" style={{ padding: '8px 12px', borderTop: '1px solid var(--line)' }}>
            {histoPret
              ? 'La semaine des espèces court du mardi au lundi. Les jours à venir ne sont pas cumulés.'
              : 'Chargement des journées de la semaine…'}
          </div>
        </Card>
      </div>
    </>
  )
}

/**
 * Entrée passe au compteur SUIVANT de la même carte — le coffre, puis la
 * feuille papier — au lieu de rendre la main. Les deux nombres de l'étape
 * s'enchaînent alors sans lâcher le clavier ni chercher le second champ.
 * Même idiome que le compteur des apports, dont le helper n'est pas exporté.
 */
function champSuivant(courant: HTMLInputElement): void {
  const zone = courant.closest('.card')
  const tous = Array.from(zone?.querySelectorAll<HTMLInputElement>('input[data-compteur]') ?? [])
  const suivant = tous[tous.indexOf(courant) + 1]
  if (suivant && !suivant.disabled) { suivant.focus(); suivant.select() } else courant.blur()
}

/**
 * Compteur d'enveloppes : entier, jamais négatif, jamais un montant.
 *
 * Le champ se traverse debout, à 7 h, avec la file qui attend. Il reprend donc
 * l'idiome du compteur des apports, qui a déjà payé ces leçons-là :
 *
 *   • un TAMPON de frappe autorise le champ vide le temps de retaper. Sans
 *     lui — c'était le cas ici — effacer « 12 » réaffiche « 0 » sous les
 *     doigts et il faut taper par-dessus un zéro qu'on n'a pas demandé ;
 *   • les deux boutons sortent du parcours de tabulation : Tab va du coffre à
 *     la feuille papier, pas de bouton en bouton ;
 *   • cliquer + ou − ne vole pas le focus du champ (mousedown neutralisé), on
 *     continue à taper juste après ;
 *   • plus de type="number" : la molette ne peut plus changer un comptage en
 *     faisant défiler la page, et le pavé numérique sort quand même sur
 *     mobile (inputMode).
 *
 * Entrée et la sortie du champ postent sans attendre les 400 ms ; Échap
 * renonce à la frappe. Aucune touche n'est capturée hors du champ.
 */
function Compteur({ valeur, sur, valider, annuler, desactive, libelle }: {
  valeur: number
  sur: (n: number) => void
  valider: () => void
  annuler: () => void
  desactive: boolean
  libelle: string
}) {
  const [tampon, setTampon] = useState<string | null>(null)
  // Échap efface le brouillon du parent, puis rend la main : le `blur` part
  // AVANT que React ait rendu, et le `valider` de la sortie de champ reposterait
  // la valeur qu'on vient d'abandonner. Un ref se lit tout de suite, lui.
  const renonce = useRef(false)

  const frapper = (brut: string) => {
    // Quatre chiffres : 9 999 enveloppes valent près de 5 M€. Au-delà, ce n'est
    // plus un comptage, c'est une faute de frappe.
    const s = brut.replace(/\D/g, '').slice(0, 4)
    setTampon(s)
    sur(s === '' ? 0 : Number.parseInt(s, 10))
  }
  const pas = (delta: number) => { setTampon(null); sur(Math.max(0, valeur + delta)) }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'stretch' }}>
      <button type="button" className="btn sm" disabled={desactive || valeur <= 0} tabIndex={-1}
              aria-label={`Retirer une enveloppe — ${libelle}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pas(-1)}>−</button>
      <input
        className="mo" inputMode="numeric" data-compteur
        value={tampon ?? String(valeur)} disabled={desactive}
        aria-label={libelle}
        onFocus={(e) => e.currentTarget.select()}
        onChange={(e) => frapper(e.target.value)}
        onBlur={() => {
          setTampon(null)
          if (renonce.current) { renonce.current = false; return }
          valider()
        }}
        onKeyDown={(e) => {
          // L'étape voisine capture des touches au niveau du document : une
          // frappe dans un compteur ne doit jamais y remonter.
          e.stopPropagation()
          const el = e.currentTarget
          // Dans les deux cas on quitte le champ : c'est la sortie de champ,
          // ci-dessus, qui poste ou qui renonce — une seule écriture, un seul
          // endroit où la décider.
          if (e.key === 'Enter') { e.preventDefault(); champSuivant(el) }
          else if (e.key === 'Escape') {
            e.preventDefault(); renonce.current = true; annuler(); el.blur()
          }
        }}
        style={{
          width: 64, textAlign: 'center', padding: '5px 6px', fontSize: 15,
          border: '1px solid var(--line2)', borderLeft: 'none', borderRight: 'none',
          background: 'var(--surface2)',
        }}
      />
      <button type="button" className="btn sm" disabled={desactive} tabIndex={-1}
              aria-label={`Ajouter une enveloppe — ${libelle}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pas(1)}>+</button>
    </span>
  )
}
